import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import {
  WALLET_AMOUNT_PRESETS,
  WALLET_MAX_CENTS,
  WALLET_MIN_CENTS,
  WALLET_THRESHOLD_PRESETS,
  IWalletResponse,
  WalletEntryType,
  currencyFormatter,
} from 'shared';

import { Badge } from '../../../common/Atoms/Badge';
import { Button } from '../../../common/Atoms/Button';
import { InputTextAddon } from '../../../common/Atoms/InputTextAddon';
import { Modal } from '../../../common/Atoms/Modal';
import { Select } from '../../../common/Atoms/Select';
import { Toggle } from '../../../common/Atoms/Toggle';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { CallPricingLink } from '../Calls/CallPricing';
import {
  useFetchWalletQuery,
  usePurchaseWalletCreditMutation,
  useUpdateWalletAutoRechargeMutation,
} from '../../services/api/subscriptionApi/subscription';

const ENTRY_LABELS: Record<WalletEntryType, string> = {
  [WalletEntryType.INCLUDED]: `Included with plan`,
  [WalletEntryType.PURCHASE]: `Purchased`,
  [WalletEntryType.AUTO_RECHARGE]: `Auto recharge`,
  [WalletEntryType.SPEND]: `Call`,
  [WalletEntryType.ADJUSTMENT]: `Adjustment`,
  [WalletEntryType.EXPIRY]: `Expired`,
};

const OTHER = `other`;

const errorMessage = (err: unknown) => (err as { data?: { message?: string } })?.data?.message || `Something went wrong, please try again`;

const inRange = (cents: number | null) => cents !== null && Number.isInteger(cents) && cents >= WALLET_MIN_CENTS && cents <= WALLET_MAX_CENTS;

interface AmountPickerProps {
  label: string;
  presets: number[];
  currency: string;
  cents: number | null;
  onChange: (cents: number | null) => void;
}

/** Preset whole amounts plus "Other" for anything in range. Works in cents, shows major units */
function AmountPicker({ label, presets, currency, cents, onChange }: AmountPickerProps) {
  const isPreset = cents !== null && presets.includes(cents / 100);
  const [custom, setCustom] = useState(cents !== null && !isPreset);
  const money = currencyFormatter(currency);

  const options = [
    ...presets.map(amount => ({ label: money(amount), value: amount })),
    { label: `Other`, value: OTHER },
  ];

  return (
    <div className={ `flex items-end gap-3` }>
      <div className={ `flex-1` }>
        <Select
          label={ label }
          options={ options }
          selected={ custom ? OTHER : (cents === null ? undefined : cents / 100) }
          onChange={ ({ value }) => {
            setCustom(value === OTHER);
            onChange(value === OTHER ? null : Number(value) * 100);
          } }
        />
      </div>
      { custom ? (
        <div className={ `flex-1` }>
          <InputTextAddon
            name={ `${label} custom` }
            dataType={ `number` }
            value={ cents === null ? `` : cents / 100 }
            description={ `${money(WALLET_MIN_CENTS / 100)} to ${money(WALLET_MAX_CENTS / 100)}` }
            onChange={ ({ value }) => onChange(value === `` ? null : Math.round(Number(value)) * 100) }
          />
        </div>
      ) : null }
    </div>
  );
}

function AddCreditModal({ subscriptionId, currency, open, setOpen }: { subscriptionId: number; currency: string; open: boolean; setOpen: (open: boolean) => void }) {
  const [amountCents, setAmountCents] = useState<number | null>(WALLET_AMOUNT_PRESETS[1] * 100);
  // One key per purchase attempt. Retrying after a timeout or server error reuses it so the card is not charged twice
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [purchase, { isLoading }] = usePurchaseWalletCreditMutation();

  useEffect(() => {
    if (open) setIdempotencyKey(crypto.randomUUID());
  }, [open, amountCents]);

  async function onBuy() {
    try {
      await purchase({ subscriptionId, amountCents: amountCents as number, idempotencyKey }).unwrap();
      toast.success(`Credit added`);
      setOpen(false);
    }
    catch (err) {
      // A decline voids the invoice, so trying again after updating the card is a new purchase
      if ((err as { status?: number })?.status === 402) setIdempotencyKey(crypto.randomUUID());

      toast.error(errorMessage(err));
    }
  }

  return (
    <Modal
      title={ `Add prepaid credit` }
      open={ open }
      setOpen={ setOpen }
    >
      <div className={ `flex flex-col gap-4` }>
        <AmountPicker
          label={ `Amount` }
          presets={ WALLET_AMOUNT_PRESETS }
          currency={ currency }
          cents={ amountCents }
          onChange={ setAmountCents }
        />
        <Paragraph variant={ `help` }>
          { `Charged now to the card on your subscription. Purchased credit is non refundable and expires after 12 months.` }
        </Paragraph>
        <div className={ `flex justify-end` }>
          <Button
            onClick={ onBuy }
            loading={ isLoading }
            disabled={ !inRange(amountCents) }
          >
            { amountCents ? `Pay ${currencyFormatter(currency)(amountCents / 100)}` : `Pay` }
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AutoRechargeModal({ subscriptionId, wallet, open, setOpen }: { subscriptionId: number; wallet: IWalletResponse; open: boolean; setOpen: (open: boolean) => void }) {
  const { autoRecharge, currency } = wallet;
  const [enabled, setEnabled] = useState(autoRecharge.enabled);
  const [thresholdCents, setThresholdCents] = useState(autoRecharge.thresholdCents ?? WALLET_THRESHOLD_PRESETS[0] * 100);
  const [amountCents, setAmountCents] = useState(autoRecharge.amountCents ?? WALLET_AMOUNT_PRESETS[1] * 100);
  const [capCents, setCapCents] = useState(autoRecharge.monthlyCapCents ?? WALLET_AMOUNT_PRESETS[2] * 100);
  const [update, { isLoading }] = useUpdateWalletAutoRechargeMutation();

  useEffect(() => {
    if (open) setEnabled(autoRecharge.enabled);
  }, [open, autoRecharge.enabled]);

  const valid = !enabled || (inRange(thresholdCents) && inRange(amountCents) && capCents !== null && capCents >= (amountCents ?? 0));

  async function onSave() {
    try {
      await update({
        subscriptionId,
        update: { enabled, thresholdCents, amountCents, monthlyCapCents: capCents },
      }).unwrap();

      setOpen(false);
    }
    catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <Modal
      title={ `Auto recharge` }
      open={ open }
      setOpen={ setOpen }
    >
      <div className={ `flex flex-col gap-4` }>
        <Toggle
          label={ `Top up automatically` }
          description={ `When your balance drops below the threshold, charge the card on your subscription.` }
          checked={ enabled }
          onChange={ ({ value }) => setEnabled(value) }
        />
        <AmountPicker
          label={ `When balance drops below` }
          presets={ WALLET_THRESHOLD_PRESETS }
          currency={ currency }
          cents={ thresholdCents }
          onChange={ setThresholdCents }
        />
        <AmountPicker
          label={ `Recharge amount` }
          presets={ WALLET_AMOUNT_PRESETS }
          currency={ currency }
          cents={ amountCents }
          onChange={ setAmountCents }
        />
        <InputTextAddon
          label={ `Monthly spend cap` }
          name={ `monthly spend cap` }
          dataType={ `number` }
          value={ capCents === null ? `` : capCents / 100 }
          description={ `Auto recharge stops for the rest of the billing month once this much has been charged. Must be at least the recharge amount.` }
          onChange={ ({ value }) => setCapCents(value === `` ? null : Math.round(Number(value)) * 100) }
        />
        <div className={ `flex justify-end` }>
          <Button
            onClick={ onSave }
            loading={ isLoading }
            disabled={ !valid }
          >
            { `Save` }
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** canPurchase is false for inactive subs, which can still see their balance but not add credit */
export function Wallet({ subscriptionId, canPurchase }: { subscriptionId: number; canPurchase: boolean }) {
  const { data: wallet } = useFetchWalletQuery({ subscriptionId });
  const [showAddCredit, setShowAddCredit] = useState(false);
  const [showAutoRecharge, setShowAutoRecharge] = useState(false);

  // The calls page links here with #wallet, which only exists once the wallet loads
  useEffect(() => {
    if (wallet && window.location.hash === `#wallet`) {
      document.getElementById(`wallet`)?.scrollIntoView({ behavior: `smooth` });
    }
  }, [Boolean(wallet)]);

  if (!wallet) return null;

  const money = (cents: number) => currencyFormatter(wallet.currency)(cents / 100);
  const { autoRecharge } = wallet;

  return (
    <div id={ `wallet` }>
      <Paragraph variant={ `primary` }>
        { `Prepaid Credit` }
      </Paragraph>
      <Paragraph variant={ `help` }>
        { wallet.includedMonthlyCents
          ? `Used for calls. Your plan includes ${money(wallet.includedMonthlyCents)} each billing month, which does not roll over. Purchased credit lasts 12 months.`
          : `Used for calls. Purchased credit lasts 12 months.` }
      </Paragraph>
      <div className={ `mt-3 rounded-lg border border-gray-200` }>
        <div className={ `flex items-center justify-between border-b border-gray-100 px-5 py-4` }>
          <div className={ `flex items-center gap-3` }>
            <span className={ `text-sm font-semibold text-paidniceGreen-800` }>{ `Balance` }</span>
            <span className={ `text-sm text-gray-700` }>{ money(wallet.balanceCents) }</span>
            <span className={ `text-sm` }>
              <CallPricingLink currency={ wallet.currency } />
            </span>
          </div>
          { canPurchase ? (
            <button
              className={ `cursor-pointer text-sm font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-700` }
              onClick={ () => setShowAddCredit(true) }
            >
              { `Add credit` }
            </button>
          ) : (
            <span className={ `text-xs text-gray-500` }>{ `Credit can be added while the subscription is active` }</span>
          ) }
        </div>
        <div className={ `flex items-center justify-between border-b border-gray-100 px-5 py-4` }>
          <div className={ `flex flex-col` }>
            <span className={ `text-sm font-semibold text-paidniceGreen-800` }>{ `Auto recharge` }</span>
            { autoRecharge.enabled && autoRecharge.amountCents !== null && autoRecharge.thresholdCents !== null && autoRecharge.monthlyCapCents !== null ? (
              <span className={ `text-xs text-gray-500` }>
                { `Adds ${money(autoRecharge.amountCents)} below ${money(autoRecharge.thresholdCents)}. ${money(autoRecharge.spentThisMonthCents)} of ${money(autoRecharge.monthlyCapCents)} cap used this month.` }
              </span>
            ) : null }
          </div>
          <div className={ `flex items-center gap-4` }>
            <Badge
              color={ autoRecharge.enabled ? `green` : `gray` }
              message={ autoRecharge.enabled ? `On` : `Off` }
              size={ `md` }
            />
            { canPurchase ? (
              <button
                className={ `cursor-pointer text-sm font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-700` }
                onClick={ () => setShowAutoRecharge(true) }
              >
                { `Edit` }
              </button>
            ) : null }
          </div>
        </div>
        <div className={ `px-5 py-4` }>
          { wallet.entries.length ? (
            <table className={ `w-full text-left text-sm` }>
              <thead className={ `text-xs text-gray-500` }>
                <tr>
                  <th className={ `pb-2 font-medium` }>{ `Date` }</th>
                  <th className={ `pb-2 font-medium` }>{ `Type` }</th>
                  <th className={ `pb-2 font-medium` }>{ `Amount` }</th>
                  <th className={ `pb-2 font-medium` }>{ `Remaining` }</th>
                  <th className={ `pb-2 font-medium` }>{ `Expires` }</th>
                </tr>
              </thead>
              <tbody className={ `text-gray-700` }>
                { wallet.entries.map(entry => (
                  <tr key={ entry.id }>
                    <td className={ `py-1` }>{ format(new Date(entry.createdAt), `d MMM yyyy`) }</td>
                    <td className={ `py-1` }>
                      { entry.hostedInvoiceUrl ? (
                        <a
                          className={ `cursor-pointer underline` }
                          href={ entry.hostedInvoiceUrl }
                          target={ `_blank` }
                          rel={ `noreferrer` }
                        >
                          { ENTRY_LABELS[entry.type] }
                        </a>
                      ) : ENTRY_LABELS[entry.type] }
                    </td>
                    <td className={ `py-1 ${entry.amountCents < 0 ? `text-paidniceRed-700` : ``}` }>{ money(entry.amountCents) }</td>
                    <td className={ `py-1` }>{ entry.remainingCents === null ? `` : money(entry.remainingCents) }</td>
                    <td className={ `py-1` }>
                      { entry.expiresAt ? (new Date(entry.expiresAt) > new Date() ? format(new Date(entry.expiresAt), `d MMM yyyy`) : `Expired`) : `` }
                    </td>
                  </tr>
                )) }
              </tbody>
            </table>
          ) : (
            <Paragraph variant={ `help` }>{ `No credit yet.` }</Paragraph>
          ) }
        </div>
      </div>
      <AddCreditModal
        subscriptionId={ subscriptionId }
        currency={ wallet.currency }
        open={ showAddCredit }
        setOpen={ setShowAddCredit }
      />
      <AutoRechargeModal
        subscriptionId={ subscriptionId }
        wallet={ wallet }
        open={ showAutoRecharge }
        setOpen={ setShowAutoRecharge }
      />
    </div>
  );
}
